// src/pages/Pos/PosDevicesPage.jsx — أجهزة نقطة البيع (الأوفلاين) والفواتير المرفوضة أثناء الترحيل
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Typography, TextField, Button, IconButton, Tooltip, CircularProgress, MenuItem,
  Dialog, DialogContent, DialogActions, Tabs, Tab,
} from "@mui/material";
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import WifiIcon from "@mui/icons-material/Wifi";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DoNotDisturbOnOutlinedIcon from "@mui/icons-material/DoNotDisturbOnOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import axios from "../../axios";
import { ReportPage, fieldSx, fmtNumber } from "../../components/Reports/ReportKit";

// ───────────────────────── أدوات ─────────────────────────
const errMsg = (e, fb) => {
  const d = e?.response?.data;
  return (typeof d === "string" && d) || d?.message || d?.title || fb;
};
const esc = (s) => String(s ?? "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const when = (d) => (d ? dayjs(d).format("YYYY/MM/DD HH:mm") : "—");
const ago = (d) => {
  if (!d) return "لم يتصل بعد";
  const m = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (m < 1) return "الآن";
  if (m < 60) return `قبل ${m} دقيقة`;
  const h = Math.round(m / 60);
  if (h < 48) return `قبل ${h} ساعة`;
  return `قبل ${Math.round(h / 24)} يوم`;
};

const primaryBtnSx = {
  fontWeight: 800, borderRadius: "10px", px: 2, boxShadow: "none",
  bgcolor: "#4f46e5 !important", color: "#fff !important", border: "1px solid #4f46e5 !important",
  "&:hover": { bgcolor: "#4338ca !important", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "#c7d2fe !important", color: "#fff !important", borderColor: "#c7d2fe !important" },
};
const ghostBtnSx = {
  fontWeight: 800, borderRadius: "10px", px: 2,
  bgcolor: "#fff !important", color: "#334155 !important", border: "1px solid #cbd5e1 !important",
  "&:hover": { bgcolor: "#f8fafc !important" },
};

const HEALTH = {
  online: { label: "متصل", bg: "#d1fae5", fg: "#047857", icon: <WifiIcon /> },
  stale: { label: "انقطع اليوم", bg: "#fef3c7", fg: "#b45309", icon: <WifiOffIcon /> },
  "offline-warning": { label: "منقطع أكثر من يوم", bg: "#ffe4e6", fg: "#be123c", icon: <WifiOffIcon /> },
  never: { label: "غير مربوط", bg: "#f1f5f9", fg: "#475569", icon: <LinkOffIcon /> },
};
const ISSUE_STATUS = {
  0: { label: "مفتوحة", bg: "#ffe4e6", fg: "#be123c" },
  3: { label: "بانتظار إعادة الإرسال", bg: "#e0f2fe", fg: "#0369a1" },
  1: { label: "تمت المعالجة", bg: "#d1fae5", fg: "#047857" },
  2: { label: "تم التجاهل", bg: "#f1f5f9", fg: "#475569" },
};

function Pill({ bg, fg, icon, children }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: .5, px: 1.1, py: .3, borderRadius: "999px", bgcolor: bg, color: fg, fontSize: 12, fontWeight: 800, whiteSpace: "nowrap", "& svg": { fontSize: 15 } }}>
      {icon}{children}
    </Box>
  );
}

const th = { fontWeight: 800, color: "#475569", fontSize: 13, py: 1.25, px: 1.5, bgcolor: "#f8fafc", borderBottom: "1px solid #e5e7eb", textAlign: "right", whiteSpace: "nowrap" };
const td = { py: 1.25, px: 1.5, borderBottom: "1px solid #f1f5f9", verticalAlign: "middle", fontSize: 13.5 };

// ───────────────────────── نافذة كود التسجيل ─────────────────────────
function CodeDialog({ info, onClose }) {
  if (!info) return null;
  const copy = () => {
    navigator.clipboard?.writeText(info.code).then(() => toast.success("تم نسخ الكود")).catch(() => { });
  };
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogContent dir="rtl" sx={{ pt: 3 }}>
        <Typography sx={{ fontWeight: 900, fontSize: 18, mb: .5 }}>كود ربط «{info.name}»</Typography>
        <Typography sx={{ color: "#64748b", fontSize: 13.5, mb: 2 }}>
          على جهاز الكاشير افتح <b dir="ltr">http://localhost:5090/agent</b> وأدخل عنوان السيرفر وهذا الكود.
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, py: 2, borderRadius: "14px", bgcolor: "#eef2ff", border: "1px dashed #a5b4fc" }}>
          <Typography dir="ltr" sx={{ fontFamily: "monospace", fontWeight: 900, fontSize: 30, letterSpacing: 4, color: "#3730a3" }}>{info.code}</Typography>
          <Tooltip title="نسخ"><IconButton onClick={copy}><ContentCopyOutlinedIcon /></IconButton></Tooltip>
        </Box>
        <Typography sx={{ color: "#64748b", fontSize: 12.5, mt: 1.5 }}>صالح حتى {when(info.expiresAt)} — يُستخدم مرة واحدة.</Typography>
        {info.warning && <Typography sx={{ color: "#b45309", fontSize: 12.5, mt: 1, fontWeight: 700 }}>⚠️ {info.warning}</Typography>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained" sx={primaryBtnSx}>تم</Button>
      </DialogActions>
    </Dialog>
  );
}

// ───────────────────────── إضافة / تعديل جهاز ─────────────────────────
function DeviceDialog({ device, branches, onClose, onSaved }) {
  const isNew = !device?.id;
  const [name, setName] = useState(device?.name || "");
  const [branchId, setBranchId] = useState(device?.branchId || branches[0]?.id || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("أدخل اسم الجهاز"); return; }
    if (isNew && !branchId) { toast.error("اختر الفرع"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await axios.post("/PosDevices", { name: name.trim(), branchId: Number(branchId) });
        onSaved({ code: data.registrationCode, expiresAt: data.expiresAt, name: data.name });
      } else {
        await axios.put(`/PosDevices/${device.id}`, { name: name.trim() });
        onSaved(null);
      }
    } catch (e) {
      toast.error(errMsg(e, "تعذر الحفظ"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogContent dir="rtl" sx={{ pt: 3 }}>
        <Typography sx={{ fontWeight: 900, fontSize: 18, mb: 2 }}>{isNew ? "إضافة جهاز نقطة بيع" : "تعديل اسم الجهاز"}</Typography>
        <TextField label="اسم الجهاز" placeholder="كاشير 1" fullWidth size="small" value={name} onChange={e => setName(e.target.value)} sx={{ ...fieldSx, mb: 2 }} autoFocus />
        {isNew && (
          <TextField select label="الفرع" fullWidth size="small" value={branchId} onChange={e => setBranchId(e.target.value)} sx={fieldSx}>
            {branches.map(b => <MenuItem key={b.id} value={b.id}>{b.name || b.nameAr || `فرع ${b.id}`}</MenuItem>)}
          </TextField>
        )}
        {isNew && (
          <Typography sx={{ color: "#64748b", fontSize: 12.5, mt: 1.5 }}>
            لكل جهاز رقم ثابت تُبنى عليه أرقام فواتيره (مثال: P3-000015) — الأجهزة لا تُحذف بل توقف.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} sx={ghostBtnSx}>إلغاء</Button>
        <Button onClick={save} disabled={saving} variant="contained" sx={primaryBtnSx}>{saving ? "جارٍ الحفظ..." : isNew ? "إضافة وإنشاء كود" : "حفظ"}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ───────────────────────── محتوى فاتورة مرفوضة ─────────────────────────
function IssueDialog({ issueId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`/PosDevices/issues/${issueId}`).then(r => setData(r.data)).catch(e => { toast.error(errMsg(e, "تعذر التحميل")); onClose(); });
  }, [issueId, onClose]);

  const payload = useMemo(() => {
    try { return JSON.parse(data?.payloadJson || "{}"); } catch { return null; }
  }, [data]);

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "16px" } }}>
      <DialogContent dir="rtl" sx={{ pt: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
          <Typography sx={{ fontWeight: 900, fontSize: 17, flex: 1 }}>فاتورة مرفوضة {data?.deviceSeq ? `#${data.deviceSeq}` : ""}</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>
        {!data ? <Box sx={{ py: 6, textAlign: "center" }}><CircularProgress size={28} /></Box> : (
          <>
            <Box sx={{ p: 1.5, borderRadius: "12px", bgcolor: "#fff1f2", color: "#9f1239", fontSize: 13.5, fontWeight: 700, mb: 2, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{data.error}</Box>
            {payload?.items?.length > 0 && (
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", mb: 2 }}>
                <thead><tr>
                  <Box component="th" sx={th}>الصنف</Box><Box component="th" sx={th}>الوحدة</Box>
                  <Box component="th" sx={th}>الكمية</Box><Box component="th" sx={th}>السعر</Box><Box component="th" sx={th}>الخصم</Box>
                </tr></thead>
                <tbody>
                  {payload.items.map((it, i) => (
                    <tr key={i}>
                      <Box component="td" sx={td}>{it.productId}</Box><Box component="td" sx={td}>{it.unitId}</Box>
                      <Box component="td" sx={td}>{it.quantity}</Box><Box component="td" sx={td}>{Number(it.unitPrice || 0).toFixed(2)}</Box>
                      <Box component="td" sx={td}>{Number(it.discount || 0).toFixed(2)}</Box>
                    </tr>
                  ))}
                </tbody>
              </Box>
            )}
            <Box component="pre" dir="ltr" sx={{ m: 0, p: 1.5, borderRadius: "12px", bgcolor: "#0f172a", color: "#e2e8f0", fontSize: 12, maxHeight: 320, overflow: "auto" }}>
              {payload ? JSON.stringify(payload, null, 2) : data.payloadJson}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ───────────────────────── الصفحة ─────────────────────────
export default function PosDevicesPage() {
  const companyId = parseInt(localStorage.getItem("companyId"), 10);
  const [tab, setTab] = useState(0);
  const [devices, setDevices] = useState([]);
  const [issues, setIssues] = useState([]);
  const [branches, setBranches] = useState([]);
  const [issueFilter, setIssueFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [codeInfo, setCodeInfo] = useState(null);
  const [viewIssue, setViewIssue] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, i, b] = await Promise.all([
        axios.get("/PosDevices"),
        axios.get("/PosDevices/issues"),
        axios.get(`/Branch/by-company/${companyId}`).catch(() => ({ data: [] })),
      ]);
      setDevices(Array.isArray(d.data) ? d.data : []);
      setIssues(Array.isArray(i.data) ? i.data : []);
      setBranches(Array.isArray(b.data) ? b.data : []);
    } catch (e) {
      toast.error(errMsg(e, "تعذر تحميل الأجهزة"));
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const stats = useMemo(() => ({
    total: devices.filter(d => d.isActive).length,
    online: devices.filter(d => d.health === "online").length,
    pending: devices.reduce((s, d) => s + (d.pendingCount || 0), 0),
    issues: issues.filter(i => i.status === 0).length,
  }), [devices, issues]);

  const shownIssues = useMemo(() => issues.filter(i =>
    issueFilter === "all" ? true : issueFilter === "open" ? (i.status === 0 || i.status === 3) : i.status === 1 || i.status === 2), [issues, issueFilter]);

  const newCode = async (d) => {
    try {
      const { data } = await axios.post(`/PosDevices/${d.id}/registration-code`);
      setCodeInfo({ code: data.registrationCode, expiresAt: data.expiresAt, name: d.name, warning: data.warning });
      load();
    } catch (e) { toast.error(errMsg(e, "تعذر إنشاء الكود")); }
  };

  const toggle = async (d) => {
    try {
      await axios.post(`/PosDevices/${d.id}/toggle`);
      load();
    } catch (e) { toast.error(errMsg(e, "تعذر تغيير الحالة")); }
  };

  const revoke = async (d) => {
    const ask = await Swal.fire({
      icon: "warning", title: "فصل الجهاز",
      html: `فصل <b>${esc(d.name)}</b> فورًا؟<br/><small>لن يُقبل منه أي ترحيل حتى يُربط بكود جديد.${d.pendingCount ? `<br/><b style="color:#be123c">عليه ${d.pendingCount} فاتورة لم تترحّل.</b>` : ""}</small>`,
      showCancelButton: true, confirmButtonText: "فصل", cancelButtonText: "إلغاء", confirmButtonColor: "#e11d48", reverseButtons: true, focusCancel: true,
    });
    if (!ask.isConfirmed) return;
    try {
      await axios.post(`/PosDevices/${d.id}/revoke`);
      toast.success("تم فصل الجهاز");
      load();
    } catch (e) { toast.error(errMsg(e, "تعذر فصل الجهاز")); }
  };

  const retry = async (i) => {
    try {
      const { data } = await axios.post(`/PosDevices/issues/${i.id}/retry`);
      toast.success(data?.message || "سيعاد الإرسال");
      load();
    } catch (e) { toast.error(errMsg(e, "تعذر الطلب")); }
  };

  const resolve = async (i, status) => {
    const ask = await Swal.fire({
      icon: status === 2 ? "warning" : "question",
      title: status === 2 ? "تجاهل الفاتورة" : "تمت المعالجة",
      input: "text", inputPlaceholder: "ملاحظة (اختياري)",
      html: status === 2
        ? "<small>الفاتورة لن تدخل السيرفر نهائيًا (المبيعات والمخزون والزكاة). استخدمه فقط إذا أُدخلت يدويًا.</small>"
        : "<small>تأكيد أنك عالجت الفاتورة يدويًا.</small>",
      showCancelButton: true, confirmButtonText: "تأكيد", cancelButtonText: "إلغاء", reverseButtons: true,
      confirmButtonColor: status === 2 ? "#e11d48" : "#4f46e5",
    });
    if (!ask.isConfirmed) return;
    try {
      await axios.post(`/PosDevices/issues/${i.id}/resolve`, { status, note: ask.value || null });
      load();
    } catch (e) { toast.error(errMsg(e, "تعذر الحفظ")); }
  };

  return (
    <ReportPage
      title="أجهزة نقطة البيع"
      subtitle="الأجهزة التي تعمل بالخدمة المحلية — تبيع بدون إنترنت وترحّل الفواتير تلقائيًا"
      icon={<PointOfSaleOutlinedIcon />}
      actions={[
        { label: "تحديث", icon: <RefreshIcon />, onClick: load, disabled: loading },
        { label: "إضافة جهاز", icon: <AddIcon />, onClick: () => setEditing({}), primary: true },
      ]}
      stats={[
        { label: "أجهزة مفعّلة", value: fmtNumber(stats.total), icon: <PointOfSaleOutlinedIcon />, color: "primary" },
        { label: "متصلة الآن", value: fmtNumber(stats.online), icon: <WifiIcon />, color: "success" },
        { label: "فواتير لم تترحّل", value: fmtNumber(stats.pending), icon: <CloudUploadOutlinedIcon />, color: stats.pending ? "warning" : "slate" },
        { label: "فواتير مرفوضة", value: fmtNumber(stats.issues), icon: <ReportProblemOutlinedIcon />, color: stats.issues ? "error" : "slate", highlight: stats.issues > 0 },
      ]}
    >
      <Paper elevation={0} sx={{ borderRadius: "16px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, px: 1.25, borderBottom: "1px solid #e5e7eb", bgcolor: "#fafbfc" }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ flex: 1, minHeight: 46, "& .MuiTab-root": { fontWeight: 800, minHeight: 46 } }}>
            <Tab label={`الأجهزة (${devices.length})`} />
            <Tab label={`الفواتير المرفوضة${stats.issues ? ` (${stats.issues})` : ""}`} />
          </Tabs>
          {tab === 1 && (
            <TextField select size="small" value={issueFilter} onChange={e => setIssueFilter(e.target.value)} sx={{ ...fieldSx, width: 180, my: .75 }}>
              <MenuItem value="open">المفتوحة</MenuItem>
              <MenuItem value="closed">المعالجة / المتجاهلة</MenuItem>
              <MenuItem value="all">الكل</MenuItem>
            </TextField>
          )}
        </Box>

        {loading ? (
          <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={30} /></Box>
        ) : tab === 0 ? (
          devices.length === 0 ? (
            <Box sx={{ py: 7, textAlign: "center", color: "#94a3b8" }}>
              <PointOfSaleOutlinedIcon sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
              <Typography sx={{ fontWeight: 800, color: "#64748b" }}>لا توجد أجهزة بعد</Typography>
              <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: .5 }}>ثبّت الخدمة المحلية على جهاز الكاشير ثم أضفه هنا لتحصل على كود الربط.</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditing({})} sx={{ ...primaryBtnSx, mt: 1.5 }}>إضافة أول جهاز</Button>
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
                <thead>
                  <tr>
                    <Box component="th" sx={th}>الجهاز</Box>
                    <Box component="th" sx={th}>الفرع</Box>
                    <Box component="th" sx={th}>الاتصال</Box>
                    <Box component="th" sx={th}>آخر ترحيل</Box>
                    <Box component="th" sx={th}>معلّقة</Box>
                    <Box component="th" sx={th}>مرفوضة</Box>
                    <Box component="th" sx={th}>آخر رقم</Box>
                    <Box component="th" sx={th}></Box>
                  </tr>
                </thead>
                <tbody>
                  {devices.map(d => {
                    const h = d.isActive ? (HEALTH[d.health] ?? HEALTH.never) : { label: "موقوف", bg: "#f1f5f9", fg: "#94a3b8", icon: <PowerSettingsNewIcon /> };
                    const oldPending = d.oldestPendingAt && (Date.now() - new Date(d.oldestPendingAt).getTime()) > 20 * 3600 * 1000;
                    return (
                      <Box component="tr" key={d.id} sx={{ opacity: d.isActive ? 1 : .6, "&:hover": { bgcolor: "#fafbff" } }}>
                        <Box component="td" sx={td}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                            <Box sx={{ width: 38, height: 38, borderRadius: "11px", display: "grid", placeItems: "center", bgcolor: "#eef2ff", color: "#4f46e5", fontWeight: 900, flexShrink: 0 }}>{d.deviceNo}</Box>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 800, fontSize: 14.5 }}>{d.name}</Typography>
                              <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>{d.machineName || "—"}{d.appVersion ? ` · v${d.appVersion}` : ""}</Typography>
                            </Box>
                          </Box>
                        </Box>
                        <Box component="td" sx={td}>{d.branchName || d.branchId}</Box>
                        <Box component="td" sx={td}>
                          <Pill bg={h.bg} fg={h.fg} icon={h.icon}>{h.label}</Pill>
                          <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: .4 }}>{ago(d.lastSeenAt)}</Typography>
                        </Box>
                        <Box component="td" sx={td}>{when(d.lastSyncAt)}</Box>
                        <Box component="td" sx={td}>
                          {d.pendingCount
                            ? <Pill bg={oldPending ? "#ffe4e6" : "#fef3c7"} fg={oldPending ? "#be123c" : "#b45309"}>{fmtNumber(d.pendingCount)}{oldPending ? " · أقدم من 20 ساعة" : ""}</Pill>
                            : <Typography sx={{ color: "#94a3b8" }}>0</Typography>}
                        </Box>
                        <Box component="td" sx={td}>
                          {d.openIssues
                            ? <Box component="button" type="button" onClick={() => setTab(1)} sx={{ border: 0, bgcolor: "transparent", p: 0, cursor: "pointer" }}><Pill bg="#ffe4e6" fg="#be123c">{d.openIssues}</Pill></Box>
                            : <Typography sx={{ color: "#94a3b8" }}>0</Typography>}
                        </Box>
                        <Box component="td" sx={{ ...td, fontFamily: "monospace" }}>{d.lastSeq || "—"}</Box>
                        <Box component="td" sx={{ ...td, whiteSpace: "nowrap", textAlign: "left" }}>
                          {d.registrationCode && (
                            <Tooltip title="عرض كود الربط الحالي">
                              <Button size="small" onClick={() => setCodeInfo({ code: d.registrationCode, expiresAt: d.registrationCodeExpiresAt, name: d.name })}
                                sx={{ ...ghostBtnSx, py: .25, px: 1, mr: .5, fontFamily: "monospace" }}>{d.registrationCode}</Button>
                            </Tooltip>
                          )}
                          <Tooltip title="كود ربط جديد (تثبيت على جهاز آخر)"><span><IconButton size="small" onClick={() => newCode(d)} disabled={!d.isActive}><KeyOutlinedIcon fontSize="small" /></IconButton></span></Tooltip>
                          <Tooltip title="تعديل الاسم"><IconButton size="small" onClick={() => setEditing(d)}><EditOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                          <Tooltip title={d.isActive ? "إيقاف" : "تفعيل"}><IconButton size="small" onClick={() => toggle(d)} sx={{ color: d.isActive ? "#059669" : "#94a3b8" }}><PowerSettingsNewIcon fontSize="small" /></IconButton></Tooltip>
                          {d.isRegistered && <Tooltip title="فصل الجهاز فورًا"><IconButton size="small" onClick={() => revoke(d)} sx={{ color: "#e11d48" }}><LinkOffIcon fontSize="small" /></IconButton></Tooltip>}
                        </Box>
                      </Box>
                    );
                  })}
                </tbody>
              </Box>
            </Box>
          )
        ) : shownIssues.length === 0 ? (
          <Box sx={{ py: 7, textAlign: "center", color: "#94a3b8" }}>
            <CheckCircleOutlineIcon sx={{ fontSize: 48, color: "#a7f3d0", mb: 1 }} />
            <Typography sx={{ fontWeight: 800, color: "#64748b" }}>لا توجد فواتير مرفوضة</Typography>
          </Box>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 960 }}>
              <thead>
                <tr>
                  <Box component="th" sx={th}>الجهاز</Box>
                  <Box component="th" sx={th}>الرقم</Box>
                  <Box component="th" sx={th}>السبب</Box>
                  <Box component="th" sx={th}>المحاولات</Box>
                  <Box component="th" sx={th}>التاريخ</Box>
                  <Box component="th" sx={th}>الحالة</Box>
                  <Box component="th" sx={th}></Box>
                </tr>
              </thead>
              <tbody>
                {shownIssues.map(i => {
                  const st = ISSUE_STATUS[i.status] ?? ISSUE_STATUS[0];
                  const open = i.status === 0;
                  return (
                    <Box component="tr" key={i.id} sx={{ "&:hover": { bgcolor: "#fafbff" } }}>
                      <Box component="td" sx={{ ...td, fontWeight: 800 }}>{i.deviceName || i.posDeviceId}</Box>
                      <Box component="td" sx={{ ...td, fontFamily: "monospace" }}>{i.kind === "session" ? "وردية" : (i.deviceSeq ?? "—")}</Box>
                      <Box component="td" sx={{ ...td, maxWidth: 380 }}>
                        <Typography sx={{ fontSize: 13, color: "#9f1239", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{i.error}</Typography>
                        {i.resolutionNote && <Typography sx={{ fontSize: 12, color: "#64748b" }}>📝 {i.resolutionNote}</Typography>}
                      </Box>
                      <Box component="td" sx={td}>{i.attempts}</Box>
                      <Box component="td" sx={td}>{when(i.createdAt)}</Box>
                      <Box component="td" sx={td}><Pill bg={st.bg} fg={st.fg}>{st.label}</Pill></Box>
                      <Box component="td" sx={{ ...td, whiteSpace: "nowrap", textAlign: "left" }}>
                        <Tooltip title="عرض الفاتورة كما صدرت"><IconButton size="small" onClick={() => setViewIssue(i.id)}><VisibilityOutlinedIcon fontSize="small" /></IconButton></Tooltip>
                        {open && i.kind === "invoice" && <Tooltip title="إعادة الإرسال (بعد تصحيح السبب)"><IconButton size="small" onClick={() => retry(i)} sx={{ color: "#0284c7" }}><ReplayIcon fontSize="small" /></IconButton></Tooltip>}
                        {(open || i.status === 3) && <Tooltip title="تمت المعالجة يدويًا"><IconButton size="small" onClick={() => resolve(i, 1)} sx={{ color: "#059669" }}><CheckCircleOutlineIcon fontSize="small" /></IconButton></Tooltip>}
                        {(open || i.status === 3) && <Tooltip title="تجاهل"><IconButton size="small" onClick={() => resolve(i, 2)} sx={{ color: "#94a3b8" }}><DoNotDisturbOnOutlinedIcon fontSize="small" /></IconButton></Tooltip>}
                      </Box>
                    </Box>
                  );
                })}
              </tbody>
            </Box>
          </Box>
        )}
      </Paper>

      {editing && (
        <DeviceDialog
          device={editing}
          branches={branches}
          onClose={() => setEditing(null)}
          onSaved={(info) => { setEditing(null); if (info) setCodeInfo(info); load(); }}
        />
      )}
      <CodeDialog info={codeInfo} onClose={() => setCodeInfo(null)} />
      {viewIssue && <IssueDialog issueId={viewIssue} onClose={() => setViewIssue(null)} />}
    </ReportPage>
  );
}
